import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import './ProfileModal.scss';

const trimRequiredValidator = (fieldLabel, minLen) => (_, value) => {
  const trimmed = (value || '').trim();
  if (!trimmed)
    return Promise.reject(new Error(`Please enter your ${fieldLabel}`));
  if (trimmed.length < minLen)
    return Promise.reject(
      new Error(`${fieldLabel} must be at least ${minLen} characters`),
    );
  return Promise.resolve();
};

const ProfileModal = forwardRef(({ onSubmit, loading = false }, ref) => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fieldValues, setFieldValues] = useState({
    firstName: '',
    lastName: '',
  });
  const firstTrimmed = (fieldValues.firstName || '').trim();
  const lastTrimmed = (fieldValues.lastName || '').trim();
  const isFormValid = firstTrimmed.length >= 2 && lastTrimmed.length >= 2;

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    show: (initialValues = {}) => {
      const first = initialValues.firstName || '';
      const last = initialValues.lastName || '';
      form.setFieldsValue({ firstName: first, lastName: last });
      setFieldValues({ firstName: first, lastName: last });
      setVisible(true);
    },
    hide: () => {
      setVisible(false);
      form.resetFields();
      setFieldValues({ firstName: '', lastName: '' });
    },
    getForm: () => form,
  }));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const trimmedValues = {
        ...values,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      };
      console.log('Profile Form Values:', trimmedValues);

      if (onSubmit) {
        await onSubmit(trimmedValues);
      }

      message.success('Profile updated successfully!');
      setVisible(false);
      form.resetFields();
    } catch (error) {
      if (error.errorFields) {
        // Validation error - already shown by form
        console.log('Validation failed:', error);
      } else {
        console.error('Submit error:', error);
        message.error('Failed to update profile. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div className="profile-modal-title">
          <UserOutlined className="profile-icon" />
          <span>Complete Your Profile</span>
        </div>
      }
      visible={visible}
      footer={null}
      centered
      maskClosable={false}
      closable={false}
      keyboard={false}
      className="profile-modal"
      width={480}
      destroyOnClose
    >
      <div className="profile-modal-content">
        <p className="profile-modal-description">
          Please provide your name to complete your profile setup.
        </p>

        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          className="profile-form"
          onValuesChange={(_, allValues) => {
            setFieldValues({
              firstName: allValues.firstName ?? '',
              lastName: allValues.lastName ?? '',
            });
          }}
        >
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[
              { validator: trimRequiredValidator('first name', 2) },
              {
                max: 50,
                message: 'First name cannot exceed 50 characters',
              },
              {
                pattern: /^[a-zA-Z\s'-]+$/,
                message:
                  'First name can only contain letters, spaces, hyphens, and apostrophes',
              },
            ]}
          >
            <Input
              placeholder="Enter your first name"
              prefix={<UserOutlined className="input-icon" />}
              size="large"
              maxLength={50}
            />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[
              { validator: trimRequiredValidator('last name', 2) },
              {
                max: 50,
                message: 'Last name cannot exceed 50 characters',
              },
              {
                pattern: /^[a-zA-Z\s'-]+$/,
                message:
                  'Last name can only contain letters, spaces, hyphens, and apostrophes',
              },
            ]}
          >
            <Input
              placeholder="Enter your last name"
              prefix={<UserOutlined className="input-icon" />}
              size="large"
              maxLength={50}
            />
          </Form.Item>

          <div className="profile-modal-actions">
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting || loading}
              disabled={!isFormValid}
              className="submit-btn"
              size="large"
            >
              {submitting ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
});

ProfileModal.displayName = 'ProfileModal';

export default ProfileModal;
